import AddIcon from "@mui/icons-material/Add";
// TODO: wrap Alert, CircularProgress, MenuItem, Select, TextField as foundation primitives
import {
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import * as React from "react";

import {
  type TimeDisplayMode,
  useTimeDisplayMode,
  useWorkspaceTimeBounds,
} from "@/shared/components/visualization/WorkspaceCtx";
import { Button } from "@/shared/foundation/primitives";
import { useDebouncedCallback, usePopupState } from "@/shared/hooks";
import { TimeBoundsSource } from "@/shared/state/visualization/schema";

import {
  ClearFiltersAction,
  DeleteFilterAction,
  SetCombinatorAction,
  SetFilterAction,
  type ColumnFiltersAction,
} from "../columnFilters/actions";
import { ALL_COLUMNS_ID } from "../columnFilters/columnDefinition";
import { ColumnFilterPopover } from "../columnFilters/ColumnFilterPopover";
import {
  conditionMeta,
  getConditionsForDataType,
  getFilterPlaceholder,
  isDatetimeColumn,
  isValuelessCondition,
  needsSecondValue,
  shouldUseDatetimePicker,
} from "../columnFilters/conditionMeta";
import { DatetimeFilterInput } from "../columnFilters/DatetimeFilterInput";
import {
  type FilterStageColorVars,
  generateFilterStageColors,
  stageColorStyle,
} from "../columnFilters/filterStageColors";
import { formatDatetimeValue } from "../columnFilters/formatDatetimeValue";
import { isValidRegex } from "../columnFilters/regexUtils";
import {
  type ColumnFilter,
  type ColumnFiltersState,
  type FilterCondition,
  hasAllColumnsFilter,
  hasPerColumnFilters,
  isExclusivityViolation,
} from "../columnFilters/state";
import { useColumnFilterActions } from "../columnFilters/useColumnFilterActions";
import { IndexState } from "../IndexState";

import styles from "./FilterBar.module.css";
import { type StageLabelOptions, stageLabel } from "./filterLabels";
import { buildColorMap } from "./highlightUtils";

// TODO: Add component-level tests for FilterBar using Vitest browser mode
// (see ENG-2009 for browser testing infrastructure). Current test coverage
// is limited to pure logic (reducer, search engine, highlight utils).
// Key scenarios to test: filter add/delete flow, __all__ exclusivity UI
// feedback, debounce behavior, datetime picker integration, pill rendering.

interface FilterBarProps {
  hidden: boolean;
  onSearch: (
    columnFilters: Record<string, ColumnFilter>,
    signal?: AbortSignal,
  ) => void;
  columnFilters: ColumnFiltersState;
  columnFiltersDispatch: React.Dispatch<ColumnFiltersAction>;
  searchIndexState: IndexState;
  /**
   * Whether the panel has enough logs that building the search index requires
   * explicit user confirmation (memory caveat). When false, the builder row is
   * replaced with a progress indicator while building; when true, the user
   * must click "Build Index" first.
   */
  needsBuildConfirmation: boolean;
  /** Panel's total log count, used in the confirmation alert copy. */
  topicMessageCount: number;
  /** Called when the user accepts the build-index confirmation. */
  onBuildIndex: () => void;
  /** Called when the user dismisses the build-index confirmation. */
  onDismissBuildPrompt: () => void;
}

export function FilterBar({
  hidden,
  onSearch,
  searchIndexState,
  columnFilters,
  columnFiltersDispatch,
  needsBuildConfirmation,
  topicMessageCount,
  onBuildIndex,
  onDismissBuildPrompt,
}: FilterBarProps) {
  const [timeDisplayMode] = useTimeDisplayMode();
  const timeBounds = useWorkspaceTimeBounds({
    timeBoundsSource: TimeBoundsSource.FilesInWorkspace,
  });
  const earliestTime = timeBounds.earliest ?? null;
  const [selectedColumnId, setSelectedColumnId] =
    React.useState<string>(ALL_COLUMNS_ID);
  const [filterText, setFilterText] = React.useState("");
  const [filterText2, setFilterText2] = React.useState("");
  const [condition, setCondition] = React.useState<FilterCondition>("contains");

  const selectedColumn = columnFilters.columns[selectedColumnId];
  const selectedDataType = selectedColumn?.dataType;
  const availableConditions = getConditionsForDataType(selectedDataType);

  const disabled = searchIndexState !== IndexState.Built;

  // Stabilize onSearch identity so debouncedSearch doesn't change on every render.
  const onSearchRef = React.useRef(onSearch);
  onSearchRef.current = onSearch;

  const stableOnSearch = React.useCallback(
    (filters: Record<string, ColumnFilter>, signal?: AbortSignal) =>
      onSearchRef.current(filters, signal),
    [],
  );

  // Trigger search when column filters change
  const debouncedSearch = useDebouncedCallback(stableOnSearch, 300);
  const abortRef = React.useRef<AbortController | null>(null);

  React.useEffect(
    function onFiltersChange() {
      if (hidden) {
        return;
      }
      abortRef.current?.abort();
      const abort = new AbortController();
      debouncedSearch(columnFilters.filters, abort.signal);
      abortRef.current = abort;
    },
    [debouncedSearch, columnFilters.filters, hidden],
  );

  // Reset condition when column changes and the current condition is unavailable
  const handleColumnChange = React.useCallback(
    (nextColumnId: string) => {
      setSelectedColumnId(nextColumnId);
      const nextDataType = columnFilters.columns[nextColumnId]?.dataType;
      const nextConditions = getConditionsForDataType(nextDataType);
      if (!nextConditions.includes(condition)) {
        setCondition(nextConditions[0]);
      }
    },
    [columnFilters.columns, condition],
  );

  const handleAdd = () => {
    // Defense-in-depth: reject if mutual exclusivity would be violated.
    if (isExclusivityViolation(selectedColumnId, columnFilters)) {
      return;
    }
    const valueless = isValuelessCondition(condition);
    const hasTwoValues = needsSecondValue(condition);
    if (!valueless && filterText.trim() === "") {
      return;
    }
    if (hasTwoValues && filterText2.trim() === "") {
      return;
    }
    if (
      condition === "matches_regex" &&
      filterText !== "" &&
      !isValidRegex(filterText)
    ) {
      return;
    }
    // Validate numeric input for numeric conditions
    if (conditionMeta[condition].numericOnly && !valueless) {
      // Guard against hex/octal/binary literals that Number() accepts
      if (/^0[xXoObB]/.test(filterText.trim())) {
        return;
      }
      if (!Number.isFinite(Number(filterText.trim()))) {
        return;
      }
      if (hasTwoValues) {
        if (/^0[xXoObB]/.test(filterText2.trim())) {
          return;
        }
        if (!Number.isFinite(Number(filterText2.trim()))) {
          return;
        }
      }
    }
    columnFiltersDispatch(
      new SetFilterAction({
        columnId: selectedColumnId,
        value: valueless ? "" : filterText.trim(),
        condition,
        ...(hasTwoValues && { value2: filterText2.trim() }),
      }),
    );
    setFilterText("");
    setFilterText2("");
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAdd();
    }
  };

  const isRegex = condition === "matches_regex";
  const isNumeric = conditionMeta[condition].numericOnly;
  const hasRegexError =
    isRegex && filterText !== "" && !isValidRegex(filterText);
  const hideTextField = isValuelessCondition(condition);
  const showSecondValue = needsSecondValue(condition);
  const useDatetimePicker = shouldUseDatetimePicker(
    selectedDataType,
    timeDisplayMode,
    condition,
  );

  const hasActiveFilters = Object.keys(columnFilters.filters).length > 0;

  // Mutual exclusivity: when __all__ has an active filter, hide per-column
  // options; when per-column filters exist, hide __all__.
  const hasAll = hasAllColumnsFilter(columnFilters);
  const hasPerColumn = hasPerColumnFilters(columnFilters);
  const selectableColumns = React.useMemo(
    () =>
      Object.values(columnFilters.columns).filter((col) => {
        if (hasAll && col.messagePathId !== ALL_COLUMNS_ID) {
          return false;
        }
        if (hasPerColumn && col.messagePathId === ALL_COLUMNS_ID) {
          return false;
        }
        return true;
      }),
    [columnFilters.columns, hasAll, hasPerColumn],
  );

  // Auto-switch to first available column when mutual exclusivity hides the
  // current selection (e.g. user adds a per-column filter while "All columns"
  // is selected).
  React.useEffect(
    function syncSelectedColumnWithSelectableOptions() {
      const isSelectable = selectableColumns.some(
        (col) => col.messagePathId === selectedColumnId,
      );
      if (!isSelectable && selectableColumns.length > 0) {
        handleColumnChange(selectableColumns[0].messagePathId);
      }
    },
    [selectableColumns, selectedColumnId, handleColumnChange],
  );

  if (hidden) {
    return null;
  }

  const showIndexAlert = searchIndexState !== IndexState.Built;

  return (
    <div className={styles.filterBar}>
      {showIndexAlert ? (
        <SearchIndexAlert
          searchIndexState={searchIndexState}
          needsBuildConfirmation={needsBuildConfirmation}
          topicMessageCount={topicMessageCount}
          onBuildIndex={onBuildIndex}
          onDismiss={onDismissBuildPrompt}
        />
      ) : (
        <div className={styles.builderRow}>
          <Select
            size="small"
            value={selectedColumnId}
            onChange={(event) => handleColumnChange(event.target.value)}
            disabled={disabled}
            className={styles.columnSelect}
            aria-label="Filter column"
          >
            {selectableColumns.map((col) => (
              <MenuItem key={col.messagePathId} value={col.messagePathId}>
                {col.name}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={condition}
            onChange={(event) =>
              setCondition(event.target.value as FilterCondition)
            }
            disabled={disabled}
            className={styles.conditionSelect}
            aria-label="Filter condition"
          >
            {availableConditions.map((key) => (
              <MenuItem key={key} value={key}>
                {conditionMeta[key].label}
              </MenuItem>
            ))}
          </Select>
          {useDatetimePicker &&
            (showSecondValue ? (
              <DatetimeFilterInput
                mode="between"
                layout="split"
                from={filterText}
                to={filterText2}
                onChange={(from, to) => {
                  setFilterText(from);
                  setFilterText2(to);
                }}
                timeDisplayMode={timeDisplayMode}
                earliestTime={earliestTime}
                textFieldProps={{ className: styles.filterInput, disabled }}
              />
            ) : (
              <DatetimeFilterInput
                mode="single"
                value={filterText}
                onChange={(v) => setFilterText(v)}
                timeDisplayMode={timeDisplayMode}
                earliestTime={earliestTime}
                textFieldProps={{ className: styles.filterInput, disabled }}
              />
            ))}
          {!useDatetimePicker && !hideTextField && (
            <TextField
              size="small"
              className={styles.filterInput}
              type={
                isNumeric && !isDatetimeColumn(selectedDataType)
                  ? "number"
                  : "text"
              }
              placeholder={getFilterPlaceholder(isRegex, showSecondValue)}
              value={
                isDatetimeColumn(selectedDataType) && isNumeric
                  ? formatDatetimeValue(
                      filterText,
                      timeDisplayMode,
                      earliestTime,
                    )
                  : filterText
              }
              onChange={(event) => setFilterText(event.target.value)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              error={hasRegexError}
              aria-label="Filter value"
            />
          )}
          {!useDatetimePicker && showSecondValue && (
            <TextField
              size="small"
              className={styles.filterInput}
              type={isDatetimeColumn(selectedDataType) ? "text" : "number"}
              placeholder="Max"
              value={
                isDatetimeColumn(selectedDataType)
                  ? formatDatetimeValue(
                      filterText2,
                      timeDisplayMode,
                      earliestTime,
                    )
                  : filterText2
              }
              onChange={(event) => setFilterText2(event.target.value)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              aria-label="Filter maximum value"
            />
          )}
          <Button
            variant="contained"
            size="small"
            onClick={handleAdd}
            disabled={
              disabled ||
              (!isValuelessCondition(condition) && filterText.trim() === "") ||
              (showSecondValue && filterText2.trim() === "") ||
              hasRegexError
            }
            startIcon={<AddIcon />}
            aria-label="Add filter"
          >
            Add
          </Button>
        </div>
      )}
      {hasActiveFilters && (
        <ActiveFilterPills
          columnFilters={columnFilters}
          columnFiltersDispatch={columnFiltersDispatch}
          timeDisplayMode={timeDisplayMode}
          earliestTime={earliestTime}
        />
      )}
    </div>
  );
}

function ActiveFilterPills({
  columnFilters,
  columnFiltersDispatch,
  timeDisplayMode,
  earliestTime,
}: {
  columnFilters: ColumnFiltersState;
  columnFiltersDispatch: React.Dispatch<ColumnFiltersAction>;
  timeDisplayMode: TimeDisplayMode;
  earliestTime: bigint | null;
}) {
  // Sort by column ID for stable pill ordering across re-renders
  const activeFilterKeys = Object.keys(columnFilters.filters).sort();

  const colorMap = React.useMemo(
    () => buildColorMap(columnFilters.filters),
    [columnFilters.filters],
  );
  const stageColors = React.useMemo(
    () => generateFilterStageColors(colorMap.size),
    [colorMap.size],
  );

  return (
    <div className={styles.pillsRow}>
      {activeFilterKeys.map((columnId) => (
        <FilterPill
          key={columnId}
          columnId={columnId}
          columnFilters={columnFilters}
          columnFiltersDispatch={columnFiltersDispatch}
          colorMap={colorMap}
          stageColors={stageColors}
          timeDisplayMode={timeDisplayMode}
          earliestTime={earliestTime}
        />
      ))}
      {activeFilterKeys.length >= 1 && (
        <button
          className={styles.clearAll}
          onClick={() => columnFiltersDispatch(new ClearFiltersAction())}
          aria-label="Clear all filters"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

function FilterPill({
  columnId,
  columnFilters,
  columnFiltersDispatch,
  colorMap,
  stageColors,
  timeDisplayMode,
  earliestTime,
}: {
  columnId: string;
  columnFilters: ColumnFiltersState;
  columnFiltersDispatch: React.Dispatch<ColumnFiltersAction>;
  colorMap: Map<string, number>;
  stageColors: FilterStageColorVars[];
  timeDisplayMode: TimeDisplayMode;
  earliestTime: bigint | null;
}) {
  const popup = usePopupState();
  const column = columnFilters.columns[columnId];
  const filter = columnFilters.filters[columnId];

  const { onStageChange, onStageAdd, onStageDelete } = useColumnFilterActions(
    columnId,
    columnFiltersDispatch,
  );

  const currentCombinator = filter.combinator;

  const onCombinatorChange = (combinator: "or" | "and") => {
    columnFiltersDispatch(new SetCombinatorAction({ columnId, combinator }));
  };

  const labelOptions: StageLabelOptions = {
    dataType: column?.dataType,
    timeDisplayMode,
    earliestTime,
  };

  const stripRef = React.useRef<HTMLDivElement>(null);

  return (
    <>
      <div className={styles.powerlineStrip} ref={stripRef}>
        <span className={styles.columnLabel}>
          {column?.name ?? columnId}:
          <button
            className={styles.deleteColumnButton}
            onClick={() =>
              columnFiltersDispatch(new DeleteFilterAction({ columnId }))
            }
            title="Remove filter"
            aria-label={`Remove filter for ${column?.name ?? columnId}`}
          >
            ×
          </button>
        </span>
        {filter.stages.map((stage, index) => {
          const colorIndex = colorMap.get(`${columnId}:${index}`);
          return (
            <React.Fragment key={`${stage.condition}-${stage.value}-${index}`}>
              {index > 0 && (
                <button
                  className={styles.combinatorSegment}
                  onClick={() =>
                    onCombinatorChange(
                      currentCombinator === "or" ? "and" : "or",
                    )
                  }
                  title={`Switch to ${currentCombinator === "or" ? "AND" : "OR"}`}
                  aria-label={`Toggle filter combinator, currently ${currentCombinator === "or" ? "OR" : "AND"}`}
                >
                  {currentCombinator === "or" ? "OR" : "AND"}
                </button>
              )}
              <button
                className={styles.stageSegment}
                style={
                  colorIndex !== undefined
                    ? stageColorStyle(colorIndex, stageColors)
                    : undefined
                }
                onClick={() => popup.open({ anchorEl: stripRef.current })}
                title={stageLabel(stage, labelOptions)}
                aria-label={`Filter: ${column?.name ?? columnId} ${stageLabel(stage, labelOptions)}`}
              >
                {stageLabel(stage, labelOptions)}
              </button>
            </React.Fragment>
          );
        })}
      </div>
      <ColumnFilterPopover
        popup={popup}
        columnName={column?.name ?? columnId}
        columnId={columnId}
        filters={columnFilters.filters}
        stages={filter.stages}
        dataType={column?.dataType}
        timeDisplayMode={timeDisplayMode}
        combinator={currentCombinator}
        onStageChange={onStageChange}
        onStageAdd={onStageAdd}
        onStageDelete={onStageDelete}
        onCombinatorChange={onCombinatorChange}
      />
    </>
  );
}

/**
 * Rendered in place of the filter builder row when the search index is empty
 * or building. Pills continue to render below this alert so that filters
 * loaded from saved layouts remain visible while the index is being built.
 */
function SearchIndexAlert({
  searchIndexState,
  needsBuildConfirmation,
  topicMessageCount,
  onBuildIndex,
  onDismiss,
}: {
  searchIndexState: IndexState;
  needsBuildConfirmation: boolean;
  topicMessageCount: number;
  onBuildIndex: () => void;
  onDismiss: () => void;
}) {
  // Empty + large log count: prompt before building. LogPanel does not
  // auto-build in this case, so the alert persists until the user clicks
  // Build Index or Dismiss.
  if (searchIndexState === IndexState.Empty && needsBuildConfirmation) {
    return (
      <Alert
        severity="warning"
        className={styles.indexAlert}
        action={
          <span className={styles.indexAlertActions}>
            <Button
              variant="text"
              size="small"
              color="inherit"
              onClick={onBuildIndex}
            >
              Build Index
            </Button>
            <Button
              variant="text"
              size="small"
              color="inherit"
              onClick={onDismiss}
            >
              Dismiss
            </Button>
          </span>
        }
      >
        Filtering requires building a search index for{" "}
        {topicMessageCount.toLocaleString()} logs. This may take a while and may
        be constrained by available memory to your browser.
      </Alert>
    );
  }

  // Otherwise: actively building, or empty + small count (transient — LogPanel
  // auto-builds on open). Show a progress indicator either way so the UI
  // doesn't flicker as the state transitions from Empty to Building.
  return (
    <Alert
      severity="info"
      icon={<CircularProgress size="1rem" />}
      className={styles.indexAlert}
    >
      Building search index…
    </Alert>
  );
}
